import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator,
} from '@/components/ui/command';
import {
  Home, Map, Activity, Shield, BarChart3, Columns, FileText, Settings,
  Users, Zap, BookOpen, Moon, Keyboard, Filter, Plug, AlertTriangle,
  Eye, Trophy, Megaphone, Rocket, Palette, CreditCard, Lock, Webhook,
  Key, Network, MonitorCheck, Bug, FolderOpen, Calculator, Brain,
  ScrollText, DollarSign, Package, Download, FlaskConical, GitBranch,
} from 'lucide-react';

const PAGES = [
  { label: 'Home', icon: Home, path: '/' },
  { label: 'Decision Engine', icon: Activity, path: '/engine' },
  { label: 'Mapping Studio', icon: Map, path: '/studio' },
  { label: 'Saved Mappings', icon: FolderOpen, path: '/mappings' },
  { label: 'Schema Graph', icon: Network, path: '/graph' },
  { label: 'Observability', icon: MonitorCheck, path: '/observability' },
  { label: 'Rules Engine', icon: Shield, path: '/rules' },
  { label: 'Sales Funnel', icon: Filter, path: '/funnel' },
  { label: 'CRM Connections', icon: Plug, path: '/crm' },
  { label: 'Objections', icon: AlertTriangle, path: '/objections' },
  { label: 'Ride-Along', icon: Eye, path: '/ride-along' },
  { label: 'Leaderboard', icon: Trophy, path: '/leaderboard' },
  { label: 'Campaigns', icon: Megaphone, path: '/campaigns' },
  { label: 'Launches', icon: Rocket, path: '/launches' },
  { label: 'Brand Center', icon: Palette, path: '/brand' },
  { label: 'ROI Dashboard', icon: BarChart3, path: '/dashboard' },
  { label: 'Dual Dashboard', icon: Columns, path: '/dashboard/compare' },
  { label: 'Adoption', icon: Users, path: '/adoption' },
  { label: 'Corrections', icon: Activity, path: '/corrections' },
  { label: 'Usage & Billing', icon: Zap, path: '/usage' },
  { label: 'Admin Panel', icon: Settings, path: '/admin' },
  { label: 'AI Governance', icon: FileText, path: '/governance' },
  { label: 'Team', icon: Users, path: '/team' },
  { label: 'API Keys', icon: Key, path: '/api-keys' },
  { label: 'Documentation', icon: BookOpen, path: '/docs' },
  { label: 'ROI Calculator', icon: Calculator, path: '/roi-calculator' },
  { label: 'Pricing', icon: CreditCard, path: '/pricing' },
  { label: 'Webhooks', icon: Webhook, path: '/webhooks' },
  { label: 'Issue Tracker', icon: Bug, path: '/issues' },
  { label: 'Privacy & Security', icon: Lock, path: '/privacy' },
  { label: 'Pattern Library', icon: Brain, path: '/patterns' },
  { label: 'Efficiency Dashboard', icon: Zap, path: '/efficiency' },
  { label: 'Centralized Logs', icon: ScrollText, path: '/logs' },
  { label: 'Cost Management', icon: DollarSign, path: '/costs' },
  { label: 'Solution Templates', icon: Package, path: '/templates' },
  { label: 'Anomaly Detection', icon: Eye, path: '/anomalies' },
  { label: 'Bulk Import/Export', icon: Download, path: '/bulk' },
  { label: 'Data Lineage', icon: GitBranch, path: '/lineage' },
  { label: 'Quality Dashboard', icon: FlaskConical, path: '/quality' },
  { label: 'Security Center', icon: Shield, path: '/security' },
];

const DOMAINS = [
  { label: 'Collections Domain', path: '/rules', icon: Zap },
  { label: 'Sales Domain', path: '/rules', icon: BarChart3 },
  { label: 'Support Domain', path: '/rules', icon: Users },
  { label: 'HR Domain', path: '/rules', icon: Users },
  { label: 'L&D Domain', path: '/rules', icon: BookOpen },
  { label: 'Operations Domain', path: '/rules', icon: Settings },
];

export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const navigate = useNavigate();

  const go = (path: string) => {
    navigate(path);
    onOpenChange(false);
  };

  const toggleDark = () => {
    const isDark = document.documentElement.classList.toggle('dark');
    localStorage.setItem('sf-theme', isDark ? 'dark' : 'light');
    onOpenChange(false);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search pages, domains, actions…" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup heading="Pages">
          {PAGES.map(p => (
            <CommandItem key={p.path + p.label} onSelect={() => go(p.path)} className="gap-2">
              <p.icon className="h-4 w-4 text-muted-foreground" />
              {p.label}
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading="Intelligence Domains">
          {DOMAINS.map(d => (
            <CommandItem key={d.label} onSelect={() => go(d.path)} className="gap-2">
              <d.icon className="h-4 w-4 text-muted-foreground" />
              {d.label}
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading="Actions">
          <CommandItem onSelect={toggleDark} className="gap-2">
            <Moon className="h-4 w-4 text-muted-foreground" />
            Toggle Dark Mode
          </CommandItem>
          <CommandItem onSelect={() => go('/issues')} className="gap-2">
            <Bug className="h-4 w-4 text-muted-foreground" />
            Report an Issue
          </CommandItem>
          <CommandItem onSelect={() => { onOpenChange(false); window.dispatchEvent(new KeyboardEvent('keydown', { key: '?', metaKey: true })); }} className="gap-2">
            <Keyboard className="h-4 w-4 text-muted-foreground" />
            Keyboard Shortcuts
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
